import { supabase } from './supabase'

/**
 * Getting at the original document.
 *
 * CLAUDE.md rule 11: no permanent file URLs. Every view is a freshly signed,
 * short-lived URL, authorised by the storage policies in 0005 against the
 * requesting user's own token. A link copied out of the app stops working
 * within the quarter hour.
 *
 * The original is never modified and never replaced — corrections create a new
 * extraction row, not a new file (rule 6). What you open here is exactly the
 * bytes your father's phone produced.
 */

/** 15 minutes. Long enough to read a letter, short enough not to matter if leaked. */
const SIGNED_URL_TTL_SECONDS = 900
/** Re-sign a little early, so a URL never expires mid-read. */
const REFRESH_MARGIN_MS = 60_000

export interface DocumentPage {
  attachmentId: string
  pageNumber: number
  url: string
  contentType: string
}

interface CacheEntry {
  url: string
  expiresAt: number
}

const cache = new Map<string, CacheEntry>()

async function signedUrl(storageKey: string): Promise<string> {
  const hit = cache.get(storageKey)
  if (hit && hit.expiresAt - REFRESH_MARGIN_MS > Date.now()) return hit.url

  const { data, error } = await supabase.storage
    .from('documents')
    .createSignedUrl(storageKey, SIGNED_URL_TTL_SECONDS)
  if (error) throw error
  if (!data?.signedUrl) throw new Error('No signed URL returned')

  cache.set(storageKey, {
    url: data.signedUrl,
    expiresAt: Date.now() + SIGNED_URL_TTL_SECONDS * 1000,
  })
  return data.signedUrl
}

/**
 * The spoken note on a document, if there is one.
 *
 * Kept apart from the pages deliberately: it is not something you look at, and
 * putting it in the page list would make it page four of three.
 */
export async function loadVoiceNote(documentId: string): Promise<string | null> {
  const { data } = await supabase
    .from('attachments')
    .select('storage_key, content_type')
    .eq('document_id', documentId)
    .is('deleted_at', null)
    .like('content_type', 'audio/%')
    .limit(1)
    .maybeSingle()
  if (!data) return null
  try {
    return await signedUrl(data.storage_key as string)
  } catch {
    return null
  }
}

/** Every page of a document, in order, ready to display. */
export async function loadDocumentPages(documentId: string): Promise<DocumentPage[]> {
  const { data, error } = await supabase
    .from('attachments')
    .select('id, storage_key, content_type, page_number')
    .eq('document_id', documentId)
    .is('deleted_at', null)
    .order('page_number', { ascending: true })
  if (error) throw error

  // Pages only. The voice note has its own way in.
  const rows = (data ?? []).filter(
    (row) => !String(row.content_type ?? '').startsWith('audio/'),
  )
  return Promise.all(
    rows.map(async (row) => ({
      attachmentId: row.id as string,
      pageNumber: (row.page_number as number | null) ?? 1,
      contentType: (row.content_type as string) ?? 'image/jpeg',
      url: await signedUrl(row.storage_key as string),
    })),
  )
}

export interface Thumbnail {
  url: string
  contentType: string
}

/**
 * Just page one — for a thumbnail in a list.
 *
 * Returns the content type as well, because a PDF has no thumbnail: putting
 * its signed URL in an <img> renders a broken-image icon, which looks like the
 * document failed to upload when in fact it is the best kind of document we
 * can receive.
 */
export async function loadThumbnail(documentId: string): Promise<Thumbnail | null> {
  const { data, error } = await supabase
    .from('attachments')
    .select('storage_key, content_type')
    .eq('document_id', documentId)
    .is('deleted_at', null)
    .not('content_type', 'like', 'audio/%')
    .order('page_number', { ascending: true })
    .limit(1)
    .maybeSingle()
  if (error || !data) return null
  try {
    return {
      url: await signedUrl(data.storage_key as string),
      contentType: (data.content_type as string) ?? 'image/jpeg',
    }
  } catch {
    return null
  }
}

/** Signed URLs are per-user. Clear on sign-out so nothing leaks across sessions. */
export function clearAttachmentCache(): void {
  cache.clear()
}
